package dev.akshaychordiya.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.content.City
import dev.akshaychordiya.fadeIn
import dev.akshaychordiya.popIn
import dev.akshaychordiya.smoothTransition
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * One plotted location.
 *
 * @param highlighted Whether the current filter includes this city.
 * @param selected Whether this city *is* the filter, which earns it a ring so it's clear what you clicked.
 */
class MapPoint(val city: City, val count: Int, val highlighted: Boolean, val selected: Boolean = false)

/** How long the dots take to finish landing, used to hold the pulse back until they have. */
private const val DOT_LANDING_MS = 900

/** Minimum tap area for a dot. Well below this and the map is unusable on a phone. */
private const val HIT_TARGET = 28

/**
 * The landmass layer.
 *
 * The map is a single SVG applied as a CSS mask rather than an `<img>`, which means the browser caches it once and
 * we can recolour it per theme with a plain `background-color` - an `<img>` could do neither.
 */
val WorldMapLandStyle = CssStyle.base {
    Modifier
        .fillMaxSize()
        .backgroundColor(colorMode.toSitePalette().border)
        .styleModifier {
            listOf("-webkit-mask", "mask").forEach { prefix ->
                property("$prefix-image", "url(/images/world-map.svg)")
                property("$prefix-size", "100% 100%")
                property("$prefix-repeat", "no-repeat")
                property("$prefix-position", "center")
            }
        }
}

// These three must stay in sync with the viewBox in world-map.svg ("0 50 1000 356"). A full equirectangular map is
// half empty ocean and Antarctica; cropping to latitudes +72..-56 keeps every inhabited region and roughly doubles
// how much of the frame the continents occupy.
private const val MAP_FULL_HEIGHT = 500.0
private const val MAP_CROP_TOP = 50.0
private const val MAP_CROP_HEIGHT = 356.0
private const val MAP_WIDTH = 1000.0

/**
 * The map is a fixed equirectangular projection, so a point's position is a pure function of its coordinates and
 * needs no measurement of the rendered element.
 */
val WorldMapStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .position(Position.Relative)
        .overflow(Overflow.Hidden)
        .styleModifier { property("aspect-ratio", "$MAP_WIDTH / $MAP_CROP_HEIGHT") }
}

val PulseKeyframes = Keyframes {
    from { Modifier.scale(1).opacity(0.55) }
    to { Modifier.scale(2.6).opacity(0) }
}

private fun leftPercent(lon: Double) = (lon + 180.0) / 360.0 * 100.0

/** Project latitude into the *cropped* frame, so dots stay glued to the coastline the crop actually shows. */
private fun topPercent(lat: Double): Double {
    val yFull = (90.0 - lat) / 180.0 * MAP_FULL_HEIGHT
    return (yFull - MAP_CROP_TOP) / MAP_CROP_HEIGHT * 100.0
}

/** Bigger dot for more talks, but sub-linear so a busy city never swamps the map. */
private fun dotSize(count: Int) = (8.0 + 3.0 * kotlin.math.sqrt(count.toDouble())).coerceAtMost(22.0)

/**
 * @param onSelect Called with the city when a dot is activated. The caller decides what that means - on /talks it
 *   toggles the list filter.
 */
@Composable
fun WorldMap(
    points: List<MapPoint>,
    modifier: Modifier = Modifier,
    onSelect: (City) -> Unit = {},
) {
    val palette = ColorMode.current.toSitePalette()

    Div(WorldMapStyle.toModifier().then(modifier).fadeIn(duration = 900).toAttrs()) {
        Div(WorldMapLandStyle.toAttrs())

        // Plot west-to-east so the dots land in a readable sweep rather than in whatever order the data happens
        // to be in.
        points.sortedBy { it.city.lon }.forEachIndexed { index, point ->
            val size = dotSize(point.count)
            val color = if (point.highlighted) palette.brandPrimary else palette.subdued
            val opacity = if (point.highlighted) 1.0 else 0.25
            val label = "${point.city.name}, ${point.city.country} - ${talkCountLabel(point.count)}"

            // Positioning wrapper. It owns `transform` for the centring offset and nothing else, so the child's
            // scale animation can never knock the dot off its coordinate.
            //
            // The wrapper is also the hit target, and it's given a generous minimum size: an 8px dot is far below
            // the ~24px that's comfortable to hit, especially on a phone.
            Div(
                Modifier
                    .position(Position.Absolute)
                    .left(leftPercent(point.city.lon).percent)
                    .top(topPercent(point.city.lat).percent)
                    .size(size.px)
                    .minWidth(HIT_TARGET.px)
                    .minHeight(HIT_TARGET.px)
                    .display(DisplayStyle.Flex)
                    .alignItems(AlignItems.Center)
                    .justifyContent(JustifyContent.Center)
                    .cursor(Cursor.Pointer)
                    // Anchor the dot on its coordinate rather than its top-left corner.
                    .translate((-50).percent, (-50).percent)
                    .onClick { onSelect(point.city) }
                    .onKeyDown { event ->
                        if (event.key == "Enter" || event.key == " ") {
                            event.preventDefault() // Space would otherwise scroll the page.
                            onSelect(point.city)
                        }
                    }
                    .attrsModifier {
                        attr("title", label)
                        attr("role", "button")
                        attr("tabindex", "0")
                        attr("aria-label", "Filter talks to $label")
                        attr("aria-pressed", point.selected.toString())
                    }
                    .toAttrs()
            ) {
                // The visible dot, sized independently of the hit target above. Colour and opacity are driven by the
                // filter, so they transition rather than snap; the landing animation only touches scale and opacity
                // and then gets out of the way.
                Div(
                    Modifier
                        .size(size.px)
                        .flexShrink(0)
                        .borderRadius(50.percent)
                        .backgroundColor(color)
                        .opacity(opacity)
                        .boxShadow(blurRadius = (size / 1.5).px, color = color.toRgb().copyf(alpha = 0.55f))
                        .thenIf(point.selected) {
                            // A ring makes the active city obvious even where dots sit close together.
                            Modifier.outline(2.px, LineStyle.Solid, color).outlineOffset(3.px)
                        }
                        .smoothTransition("background-color", "opacity", "box-shadow")
                        .popIn(index)
                        .toAttrs()
                ) {
                    // A slow halo on the highlighted dots gives the map a pulse without animating everything at
                    // once. It waits for the landing animation to finish so the two don't fight.
                    if (point.highlighted) {
                        Div(
                            Modifier
                                .fillMaxSize()
                                .borderRadius(50.percent)
                                .backgroundColor(color)
                                .animation(
                                    PulseKeyframes.toAnimation(
                                        duration = 2600.ms,
                                        timingFunction = AnimationTimingFunction.EaseOut,
                                        delay = DOT_LANDING_MS.ms,
                                        iterationCount = AnimationIterationCount.Infinite,
                                    )
                                )
                                .toAttrs()
                        )
                    }
                }
            }
        }
    }
}

private fun talkCountLabel(count: Int) = if (count == 1) "1 talk" else "$count talks"
